import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import AtriumApi, { getApiErrorMessage } from "api";
import API from "api.provider";
import { useAuth } from "hooks/useAuth";
import notify from "lib/notify";
import Default from "layouts/auth/types/Default";
import InputField from "components/fields/InputField";
import Button from "components/button";
import AlertPane from "components/alert";
import Loader from "components/loader/Loader";
import { ErrorState } from "components/states";
import { PageContainer, PageHeader } from "components/page";

function SignInDefault() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  // Auto-login if token is present in URL (from spark-onboard)
  useEffect(() => {
    const token = searchParams.get("token");
    if (token && !user) {
      login(token);
      notify.success("Welcome back");
    }
  }, [searchParams, login, user]);

  const emailRef = useRef();
  const passwordRef = useRef();
  const totpRef = useRef();

  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [slugError, setSlugError] = useState(null);
  const [slugChecked, setSlugChecked] = useState(false);

  // 2FA login state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAUserId, setTwoFAUserId] = useState(null);
  const [totpCode, setTotpCode] = useState("");

  useEffect(() => {
    const getSubdomain = () => {
      const hostname = window.location.hostname;
      const parts = hostname.split(".");
      if (parts.length >= 2) {
        return parts[0];
      }
      return null;
    };

    const checkSlug = async () => {
      const subdomain = getSubdomain();
      if (subdomain === null) {
        setSlugChecked(true);
        setSlugError("Church not found");
        return;
      }
      try {
        const res = await API.get("/slugChecker", {
          headers: {
            "x-church-slug": subdomain,
          },
        });
        if (res) {
          setSlugChecked(true); // show form
        }
      } catch (err) {
        if (err?.response?.data?.message?.toLowerCase() === "church not found") {
          setSlugError("Church not found");
        } else {
          console.error("Slug check failed:", err);
          notify.error(getApiErrorMessage(err, "Could not verify your church"));
        }
        setSlugChecked(true); // either way, we finish checking
      }
    };

    checkSlug();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();

    const trimmedEmail = email.trim();
    if (trimmedEmail !== email) setEmail(trimmedEmail);

    if (!trimmedEmail) {
      setError("Email is required");
      emailRef.current?.focus();
      return;
    }
    if (!password) {
      setError("Password is required");
      passwordRef.current?.focus();
      return;
    }
    setError("");

    try {
      setLoading(true);
      const response = await AtriumApi.post("/v1/login", {
        email: trimmedEmail,
        password,
      });

      if (response) {
        setLoading(false);

        // Check if 2FA is required
        if (response.data?.requires2FA) {
          setRequires2FA(true);
          setTwoFAUserId(response.data.userId);
          setTotpCode("");
          setTimeout(() => totpRef.current?.focus(), 100);
          return;
        }

        const token = response?.data?.token;

        if (!token) {
          console.error("No token in API response:", response.data);
          notify.error("Login failed: No authentication token received");
          return;
        }

        login(token);
        notify.success("Login successful");
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const onVerify2FA = async (e) => {
    e.preventDefault();

    if (!totpCode || totpCode.length !== 6) {
      setError("Please enter a 6-digit code");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const response = await AtriumApi.post("/v1/login/verify-2fa", {
        userId: twoFAUserId,
        code: totpCode,
      });

      if (response?.data?.token) {
        login(response.data.token);
        notify.success("Login successful");
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Invalid code"));
      setTotpCode("");
      totpRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const continueWithGoogle = () => {
    const parts = window.location.hostname.split(".");
    const slug = parts.length >= 2 ? parts[0] : "";
    const onboardingBase =
      process.env.REACT_APP_ONBOARDING_URL || "https://atrium-onboarding.com";
    window.location.href = `${onboardingBase}/google?slug=${encodeURIComponent(
      slug
    )}`;
  };

  const renderCard = () => {
    if (!slugChecked) {
      return <Loader label="Checking church details" />;
    }

    if (slugError) {
      return (
        <ErrorState
          title="Church Not Found"
          message="We couldn’t find a church at this address. Check the link and try again."
        />
      );
    }

    if (requires2FA) {
      return (
        <PageContainer>
          <PageHeader
            title="Two-Factor Authentication"
            description="Enter the 6-digit code from your authenticator app."
          />
          <form onSubmit={onVerify2FA} className="flex flex-col gap-4">
            {error && (
              <AlertPane tone="danger" className="">
                {error}
              </AlertPane>
            )}

            <InputField
              variant="auth"
              label="Verification Code"
              placeholder="000000"
              id="totp-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={totpCode}
              ref={totpRef}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                setTotpCode(val);
              }}
            />

            <Button type="submit" size="lg" fullWidth loading={loading}>
              Verify
            </Button>

            <Button
              variant="ghost"
              fullWidth
              onClick={() => {
                setRequires2FA(false);
                setTwoFAUserId(null);
                setTotpCode("");
                setError("");
              }}
            >
              Back to Sign In
            </Button>
          </form>
        </PageContainer>
      );
    }

    return (
      <PageContainer>
        <PageHeader
          title="Sign In"
          description="Enter your email and password to sign in."
        />
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          {error && (
            <AlertPane tone="danger" className="">
              {error}
            </AlertPane>
          )}

          <InputField
            variant="auth"
            label="Email"
            placeholder="Your email"
            id="email"
            type="text"
            autoComplete="email"
            value={email || ""}
            ref={emailRef}
            onChange={(e) => setEmail(e.target.value)}
          />

          <InputField
            variant="auth"
            label="Password"
            placeholder="Your password"
            id="password"
            type="password"
            autoComplete="current-password"
            value={password || ""}
            ref={passwordRef}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Button type="submit" size="lg" fullWidth loading={loading} className="mt-2">
            Sign In
          </Button>

          <a
            href="https://atrium-onboarding.com/forgot-password"
            className="focus-ring self-center rounded-control text-sm font-medium text-primary hover:underline dark:text-accent"
          >
            Forgot your password?
          </a>

          <div className="flex items-center gap-3">
            <div className="h-px w-full bg-line" />
            <p className="text-sm text-fg-muted">or</p>
            <div className="h-px w-full bg-line" />
          </div>

          <Button
            variant="secondary"
            size="lg"
            fullWidth
            leftIcon={<FcGoogle aria-hidden="true" className="h-5 w-5" />}
            onClick={continueWithGoogle}
          >
            Continue with Google
          </Button>
        </form>
      </PageContainer>
    );
  };

  return <Default maincard={<div className="w-full">{renderCard()}</div>} />;
}

export default SignInDefault;
